'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { canOptimizeUrl } from '@/lib/tool-logo';

type ScreenshotGalleryLightboxProps = {
  toolName: string;
  gallery: string[];
  mediaAlts?: string[];
};

export default function ScreenshotGalleryLightbox({
  toolName,
  gallery,
  mediaAlts = [],
}: ScreenshotGalleryLightboxProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const total = gallery.length;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveIndex(null);
      if (activeIndex === null) return;
      if (event.key === 'ArrowRight') {
        setActiveIndex((prev) => (prev === null ? 0 : (prev + 1) % total));
      }
      if (event.key === 'ArrowLeft') {
        setActiveIndex((prev) => (prev === null ? 0 : (prev - 1 + total) % total));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIndex, total]);

  const showPrev = () => setActiveIndex((prev) => (prev === null ? 0 : (prev - 1 + total) % total));
  const showNext = () => setActiveIndex((prev) => (prev === null ? 0 : (prev + 1) % total));

  return (
    <section className="rounded-2xl border border-primary/10 bg-card/80 p-5 shadow-sm backdrop-blur-sm sm:p-6 transition-colors">
      <h2 className="text-lg font-bold text-foreground">Tool Screenshots</h2>
      <div className="mt-4 flex overflow-x-auto gap-4 pb-4 snap-x hide-scrollbar -mx-5 px-5 sm:mx-0 sm:px-0">
        {gallery.map((image, index) => (
          <button
            key={image}
            type="button"
            onClick={() => setActiveIndex(index)}
            className="group relative shrink-0 w-[85vw] max-w-[280px] sm:max-w-[400px] overflow-hidden rounded-2xl border border-primary/10 bg-background/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shadow-sm transition-all hover:scale-[1.02] snap-start"
            aria-label={`Open ${toolName} screenshot ${index + 1}`}
          >
            <div className="relative aspect-video w-full overflow-hidden transition duration-500 group-hover:scale-110">
              <Image
                src={image}
                alt={mediaAlts[index] || `${toolName} screenshot ${index + 1}`}
                fill
                sizes="(max-width: 640px) 100vw, 50vw"
                className="object-cover"
                unoptimized={!canOptimizeUrl(image)}
              />
            </div>
            <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="bg-card/90 px-4 py-2 rounded-full text-xs font-bold text-primary shadow-lg scale-90 group-hover:scale-100 transition-transform">View Fullsize</span>
            </div>
          </button>
        ))}
      </div>

      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-md p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${toolName} screenshot preview`}
          onClick={() => setActiveIndex(null)}
        >
          <div
            className="relative w-full max-w-6xl rounded-3xl border border-white/10 bg-black/50 p-2 shadow-2xl sm:p-3 overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveIndex(null)}
              className="absolute right-4 top-4 z-50 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-6 sm:top-6"
              aria-label="Close lightbox"
            >
              <X className="h-6 w-6" />
            </button>
            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={showPrev}
                  className="absolute left-4 top-1/2 z-50 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:left-6"
                  aria-label="Previous screenshot"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={showNext}
                  className="absolute right-4 top-1/2 z-50 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-6"
                  aria-label="Next screenshot"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
            <div className="relative max-h-[85vh] aspect-video w-full rounded-2xl overflow-hidden shadow-2xl">
              <Image
                src={gallery[activeIndex]}
                alt={mediaAlts[activeIndex] || `${toolName} full screenshot ${activeIndex + 1}`}
                fill
                sizes="100vw"
                className="object-contain"
                unoptimized={!canOptimizeUrl(gallery[activeIndex])}
              />
            </div>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/40 backdrop-blur-md px-6 py-2 rounded-full text-xs font-bold text-white tracking-widest border border-white/10">
              {activeIndex + 1} / {total}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
